import { useEffect, useRef, type RefObject } from "react";

/**
 * A MECÂNICA DE DIÁLOGO da casa — foco preso, Esc, rolagem travada, foco
 * restaurado. Extraída do `Modal.tsx` sem trocar uma vírgula do comportamento.
 *
 * Por que ela saiu de dentro do Modal: as gavetas laterais (A Receber, A Pagar,
 * o dossiê do sócio) têm a ANATOMIA diferente — nascem coladas na direita, com
 * cabeçalho próprio — mas precisam EXATAMENTE da mesma mecânica. Copiar o efeito
 * pro `Drawer` seria criar uma segunda verdade que diverge no primeiro conserto.
 * Aqui só existe uma; o Modal e o Drawer são duas cascas dela.
 */
export function useDialogoMecanica({
  open,
  onClose,
  dismissible,
  dialogRef,
  focoInicialRef,
  escFecha = false,
}: {
  open: boolean;
  onClose: () => void;
  dismissible: boolean;
  dialogRef: RefObject<HTMLElement | null>;
  /**
   * Esc fecha SEM liberar o clique fora. Exceção pedida para o Contrato do Operador
   * (880-UX01, pedido 881/883 do dono, 22/10/2026): Escape cancela o rascunho sem gravar e o
   * foco volta ao acionador; o backdrop continua bloqueado. `dismissible` segue sendo a regra
   * geral de 02/06/2026 (nenhum modal fecha por Esc/clique fora); quem quer SÓ o Esc passa isto.
   */
  escFecha?: boolean;
  /**
   * Onde o foco deve POUSAR ao abrir, quando não é o primeiro focável.
   *
   * 🔴 Por que existe (acabamento, 29/07): o "Ver como…" tinha `autoFocus` no
   * campo de busca — a pessoa abre e já digita o nome. O primeiro focável do
   * diálogo, porém, é o X do cabeçalho; sem esta saída, ligar a mecânica jogaria
   * o cursor no X e MATARIA o `autoFocus`. Trocar de casca não pode mudar o uso.
   *
   * Se o elemento não estiver montado na hora, cai no comportamento de sempre
   * (primeiro focável) — nunca deixa o foco fora do diálogo.
   */
  focoInicialRef?: RefObject<HTMLElement | null>;
}) {
  /**
   * 🔴 DIGITAR UMA LETRA E PERDER O FOCO. (Ruddy, 14/07, usando: "os campos digitáveis não
   *    consigo digitar, apenas uma letra e tem que clicar com mouse novamente".)
   *
   * O efeito abaixo tinha `onClose` e `dismissible` nas dependências. E `onClose` é uma função
   * criada NOVA a cada render pelo componente pai (`onClose={() => ...}`). Resultado, a cada
   * tecla:
   *
   *   1. o formulário re-renderiza (a bancada tem prévia ao vivo — ela redesenha a cada tecla)
   *   2. `onClose` ganha identidade nova → o React acha que a dependência mudou
   *   3. o efeito roda de novo → e a PRIMEIRA coisa que ele faz é focar o primeiro elemento
   *      focável do diálogo — que é o botão X
   *   4. o cursor pula do campo pro X, e a pessoa tem que clicar de novo. Toda letra.
   *
   * A bomba foi armada em 13/07 (#1113), quando o X passou a chamar `fecharComCuidado` — uma
   * função recriada a cada render. O bug estava latente na dependência havia meses; a função
   * nova só acendeu o pavio.
   *
   * ⚠️ LIÇÃO (a mais cara do dia): NENHUMA auditoria de código teria achado isso. Não existe
   * "bug" no arquivo — existe um efeito que roda mais vezes do que devia. Só aparece quando
   * um humano DIGITA. Foram 300 agentes lendo código e o Ruddy achou em 1 minuto usando.
   *
   * O conserto certo é este: o gerenciamento de foco é do CICLO DE VIDA do diálogo
   * (abriu/fechou), não das props. As callbacks vão num ref — sempre a versão mais recente,
   * sem re-disparar o efeito.
   */
  const onCloseRef = useRef(onClose);
  const dismissibleRef = useRef(dismissible);
  const escFechaRef = useRef(escFecha);
  // Mesmo motivo dos dois de cima: o efeito não pode depender desta prop, senão
  // volta a refazer o foco a cada render.
  const focoInicialRefRef = useRef(focoInicialRef);
  useEffect(() => {
    onCloseRef.current = onClose;
    dismissibleRef.current = dismissible;
    escFechaRef.current = escFecha;
    focoInicialRefRef.current = focoInicialRef;
  });

  useEffect(() => {
    if (!open) return;
    // Guarda o foco anterior pra restaurar ao fechar (acessibilidade: o teclado
    // não fica perdido no <body> depois que o diálogo some).
    const prevFocus = document.activeElement as HTMLElement | null;
    const focaveis = () =>
      Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((el) => el.offsetParent !== null);
    // Foco inicial: onde quem montou o diálogo pediu (campo de busca, por
    // exemplo) e, na falta disso, o primeiro campo focável — ou o próprio
    // diálogo, se não houver nenhum.
    (focoInicialRefRef.current?.current ?? focaveis()[0] ?? dialogRef.current)?.focus();
    const handler = (e: KeyboardEvent) => {
      // Lê do ref: sempre a callback mais recente, sem prender o efeito às props.
      if (e.key === "Escape" && (dismissibleRef.current || escFechaRef.current)) { onCloseRef.current(); return; }
      if (e.key !== "Tab") return;
      // Focus-trap: o Tab não escapa pra página atrás do diálogo.
      const els = focaveis();
      if (els.length === 0) { e.preventDefault(); dialogRef.current?.focus(); return; }
      const first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
      prevFocus?.focus?.();
    };
    // ⚠️ SÓ `open`. Pôr `onClose`/`dismissible` aqui refaz o foco a cada render — e o cursor
    // pula do campo pro botão X a cada tecla. As callbacks vivem nos refs acima.
  }, [open, dialogRef]);
}
